import { fromBinary, toBinary } from "@bufbuild/protobuf";
import {
  ClientEnvelopeSchema,
  ServerEnvelopeSchema,
  type ClientEnvelope,
  type ServerEnvelope,
} from "../gen/game/v1/envelope_pb";

export type ConnectionStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "closed"
  | "error";

export type GameSocketHandlers = {
  onStatus(status: ConnectionStatus): void;
  onMessage(message: ServerEnvelope): void;
  onError(message: string): void;
};

export class GameSocket {
  private socket: WebSocket | null = null;

  constructor(private readonly handlers: GameSocketHandlers) {}

  connect(url: string, onOpen?: () => void) {
    this.disconnect();

    this.handlers.onStatus("connecting");
    const socket = new WebSocket(url);
    socket.binaryType = "arraybuffer";
    this.socket = socket;

    socket.addEventListener("open", () => {
      this.handlers.onStatus("connected");
      onOpen?.();
    });

    socket.addEventListener("close", () => {
      if (this.socket === socket) {
        this.handlers.onStatus("closed");
      }
    });

    socket.addEventListener("error", () => {
      this.handlers.onStatus("error");
      this.handlers.onError("WebSocket connection error");
    });

    socket.addEventListener("message", (event) => {
      if (!(event.data instanceof ArrayBuffer)) {
        this.handlers.onError("Expected binary protobuf WebSocket message");
        return;
      }

      try {
        this.handlers.onMessage(
          fromBinary(ServerEnvelopeSchema, new Uint8Array(event.data)),
        );
      } catch (error) {
        this.handlers.onError(
          error instanceof Error
            ? error.message
            : "Failed to decode server message",
        );
      }
    });
  }

  send(envelope: ClientEnvelope) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      this.handlers.onError("WebSocket is not connected");
      return;
    }

    this.socket.send(toBinary(ClientEnvelopeSchema, envelope));
  }

  disconnect() {
    this.socket?.close();
    this.socket = null;
  }
}
